import React, { useCallback, useContext } from 'react';
import { ampli } from '../../ampli';
import { CurrentUser } from '../../contexts/CurrentUser';

// Amplitude events for the social graph feature. All names carry the `sg_`
// prefix (snake_case family, like the `sr_as_*` search-assistant events) so
// they group together in Amplitude; property keys are snake_case like the
// rest of the app. Every event carries user_id + workspace_id and, when fired
// from inside an open graph, graph_id / graph_kind / source_type.
export type SocialGraphEvent =
  | 'sg_graph_opened'
  | 'sg_graph_generate_requested'
  | 'sg_graph_generation_cancelled'
  | 'sg_graph_deleted'
  | 'sg_analysis_mode_changed'
  | 'sg_display_setting_changed'
  | 'sg_relation_type_filter_toggled'
  | 'sg_panel_tab_opened'
  | 'sg_actor_selected'
  | 'sg_group_spotlighted'
  | 'sg_add_to_group_opened'
  | 'sg_actor_opened_externally'
  | 'sg_graph_exported'
  | 'sg_zscore_report_requested'
  | 'sg_timeline_used'
  | 'sg_fullscreen_toggled';

export type SocialGraphEventProps = Record<string, string | number | boolean | null | undefined>;

// Plain helper for places outside the graph page (graph widget hook, Graphs
// list) — pass the current user so the event carries the standard ids.
export function trackSocialGraphEvent(
  event: SocialGraphEvent,
  props: SocialGraphEventProps,
  user?: CurrentUser | null,
): void {
  ampli.track({
    event_type: event,
    event_properties: {
      user_id: user?.id,
      workspace_id: user?.workspace_id,
      ...props,
    },
  });
}

export type SocialGraphTrack = (event: SocialGraphEvent, props?: SocialGraphEventProps) => void;

// The page provides a tracker pre-bound to the open graph (id / kind / source
// type / host flags) so panels and canvas controls — which don't know which
// graph they render — can fire events without extra props. Outside the
// provider (unit renders) the hook is a no-op.
const noop: SocialGraphTrack = () => {};
export const SocialGraphAnalyticsContext = React.createContext<SocialGraphTrack>(noop);

export function useSocialGraphTrack(): SocialGraphTrack {
  return useContext(SocialGraphAnalyticsContext);
}

export function useSocialGraphTracker(base: SocialGraphEventProps, user: CurrentUser | null): SocialGraphTrack {
  // `base` is rebuilt by the caller with useMemo, so the identity is stable
  // between renders of the same graph
  return useCallback(
    (event, props) => trackSocialGraphEvent(event, { ...base, ...props }, user),
    [base, user],
  );
}
